/* eslint-disable react-hooks/exhaustive-deps */
import { literals } from "@/constants/literals";
import { getImageUrl /*requestImageDeletion */ } from "@/lib/services/images";
// import { deletePhoto } from "@/lib/services/orders";
import { useEffect, useMemo, useState, type FC } from "react";
import DetailField from "./DetailField";
import IconButton from "@/components/buttons/IconButton";
import EditionPhotoDetails from "./edition-photo-details/EditionPhotoDetails";
import RequestEditionDetails from "./RequestEditionDetails";
import PetitionMessage from "./aside-photo-details/PetittionMessage";
import Image from "./Image";
import ButtonActionsGroup from "./ButtonActionsGroup";
import { getMetaDetailData, getPhotoData } from "../utils/photo-data";
import { useUserStore } from "@/store/useUser";

interface AsidePhotoDetailProps {
  handleOnCloseAside: () => void;
  photoDetail: any;
  refetch: () => Promise<void>;
}

const showNoMandatoryValue = (value: string | null) => value || "-";

const AsidePhotoDetail: FC<AsidePhotoDetailProps> = ({
  handleOnCloseAside,
  photoDetail,
  refetch,
}) => {
  const [photoURL, setPhotoURL] = useState("");
  const [editionForm, setEditionForm] = useState(false);
  const [editionRequest, setEditionRequest] = useState(false);
  const { isSupervisor } = useUserStore();

  const disabledButtons = () =>
    !isSupervisor &&
    (photoDetail.hasDeletionRequest ||
      (photoDetail.editionRequest && photoDetail.editionRequest.length > 0));

  const detailMeta = getMetaDetailData(photoDetail);
  const detailsGrid = getPhotoData(photoDetail);

  const photoName = useMemo(
    () => photoDetail.imageKey.split("/").slice(-1)[0],
    [photoDetail],
  );

  const handleOnGetURL = async () => {
    try {
      const imageUrl = await getImageUrl(photoDetail.imageId);
      setPhotoURL(imageUrl);
    } catch (error) {
      console.error("Error fetching image URL:", error);
      setPhotoURL("");
    }
  };

  const handleOnClose = async () => {
    handleOnCloseAside();
    await refetch();
  };

  const handleOnEdit = async () => {
    setEditionForm(true);
  };

  useEffect(() => {
    if (photoDetail) {
      handleOnGetURL();
    }
  }, [photoDetail, handleOnGetURL]);

  const editionComponent = () => {
    if (editionForm) {
      return (
        <EditionPhotoDetails
          photoData={photoDetail}
          closeForm={() => setEditionForm(false)}
          refetch={refetch}
          backToTable={handleOnCloseAside}
        />
      );
    }
    return (
      <RequestEditionDetails
        closeRequest={() => setEditionRequest(false)}
        photoData={photoDetail}
        refetch={refetch}
        backToTable={handleOnCloseAside}
        canNotSend={disabledButtons()}
      />
    );
  };

  return (
    <>
      {editionForm || editionRequest ? (
        <div
          data-testid="edit-form-bg"
          className="fixed w-screen h-screen top-0 left-0 z-40 bg-transparent overflow-hidden animate-fade-to-dark"
        ></div>
      ) : null}
      <aside className="fixed top-12 right-0 h-fulldpx w-[520px] bg-ib-gray-lightest-2 z-50 overflow-y-auto border-l-2">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
          <h2 className="text-lg font-medium">{literals.photoDetail}</h2>
          <IconButton
            icon={"close"}
            className="bg-transparent text-ib-red-dark"
            iconClassName="size-6"
            onClick={() => handleOnCloseAside()}
          />
        </div>
        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Filename + actions */}
          <div className="flex justify-between items-center">
            <p>{photoName}</p>
            <ButtonActionsGroup
              edit={handleOnEdit}
              photoURL={photoURL}
              photoDetail={photoDetail}
              onClose={handleOnClose}
              disabledButtons={disabledButtons()}
              openEditionRequest={() => setEditionRequest(true)}
            />
          </div>
          {isSupervisor &&
          (photoDetail.hasDeletionRequest ||
            (photoDetail.editionRequest &&
              photoDetail.editionRequest.length > 0)) ? (
            <PetitionMessage
              petitionBy={photoDetail.requestedBy}
              petition={
                photoDetail.hasDeletionRequest
                  ? "deletion"
                  : photoDetail.editionRequest
              }
            />
          ) : null}
          <Image imageId={photoDetail.imageId} />
          {/* Meta */}
          {!editionForm && !editionRequest ? (
            <>
              <DetailField
                label={literals.description}
                value={showNoMandatoryValue(photoDetail.description)}
              />
              <div className="grid grid-cols-3 gap-6 ">
                {detailMeta.map((detail) => (
                  <DetailField
                    key={detail.label}
                    label={detail.label}
                    value={detail.value}
                  />
                ))}
              </div>
              {/* Details grid */}
              <div className="grid grid-cols-3 gap-6 ">
                {detailsGrid
                  .filter((_, index) => index < 3)
                  .map((detail) => (
                    <DetailField
                      key={detail.label}
                      label={detail.label}
                      value={detail.value}
                    />
                  ))}
              </div>
              <div className="grid grid-cols-3 gap-6 ">
                {detailsGrid
                  .filter((_, index) => index > 2 && index < 5)
                  .map((detail) => (
                    <DetailField
                      key={detail.label}
                      label={detail.label}
                      value={detail.value}
                    />
                  ))}
              </div>
              <div className="grid grid-cols-3 gap-6 ">
                {detailsGrid
                  .filter((_, index) => index > 4 && index < 7)
                  .map((detail) => (
                    <DetailField
                      key={detail.label}
                      label={detail.label}
                      value={detail.value}
                    />
                  ))}
              </div>
              <div className="grid grid-cols-3 gap-6 ">
                {detailsGrid
                  .filter((_, index) => index > 6)
                  .map((detail) => (
                    <DetailField
                      key={detail.label}
                      label={detail.label}
                      value={detail.value}
                    />
                  ))}
              </div>
            </>
          ) : (
            editionComponent()
          )}
        </div>
      </aside>

      {editionForm ? (
        <div className="fixed top-12 right-0 z-50 w-[720px] max-w-[calc(100vw-520px)] h-[calc(100vh-48px)] bg-white border-l border-ib-gray-lightest shadow-lg flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 border-b bg-white">
            <h2 className="text-lg font-medium">{literals.edit}</h2>
            <IconButton
              icon={"close"}
              className="bg-transparent text-ib-red-dark"
              iconClassName="size-6"
              onClick={() => setEditionForm(false)}
            />
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <EditionPhotoDetails
              photoData={photoDetail}
              closeForm={() => setEditionForm(false)}
              refetch={refetch}
              backToTable={handleOnCloseAside}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default AsidePhotoDetail;
